import React from "react";
import { motion } from "framer-motion";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getBlurPlaceholder } from "@/utils/blurPlaceholders";

interface ActivityCardProps {
    id?: string;
    image: string;
    logo?: string; // Optional logo image
    title: string;
    subtitle: string;
    description?: string; // Made optional - only shown in modal
    actionType: "navigate" | "external" | "modal";
    destination?: string;
    onOpenModal?: () => void;
    className?: string;
    badge?: string;
    showDescription?: boolean; // New prop to control description display
    dataOid?: string;
    priority?: boolean; // Priority loading for above-fold cards
    dayPassBadge?: boolean; // Whether activity is included in Day Pass
}

const ActivityCard: React.FC<ActivityCardProps> = ({
    id,
    image,
    logo,
    title,
    subtitle,
    description,
    actionType,
    destination,
    onOpenModal,
    className,
    badge,
    showDescription = false, // Default to false - descriptions only in modal
    dataOid,
    priority = false, // Default to false - lazy load below-fold cards
    dayPassBadge = false, // Default to false
}) => {
    const { t } = useLanguage();

    // Handle card click
    const handleCardClick = () => {
        if (onOpenModal && actionType === 'modal') {
            onOpenModal();
        }
    };

    const CardContent = (
        <motion.div
            className={`relative h-full min-h-full w-full group cursor-pointer overflow-hidden rounded-2xl shadow-2xl border border-white/10 group-hover:border-transparent transition-colors duration-300 ${className || ''}`}
            whileHover={{
                y: -10,
                boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.5)",
            }}
            transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
            }}
            data-oid={dataOid}
        >
            {/* Background Image with Zoom Effect */}
            <div className="absolute inset-0 z-0 min-h-full" data-oid="bg-img-container">
                <OptimizedImage
                    src={image}
                    alt={title}
                    className="w-full h-full min-h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
                    data-oid="optimized-img"
                    priority={priority}
                    fetchPriority={priority ? "high" : undefined}
                    blurDataURL={getBlurPlaceholder(image)}
                />
                {/* Shine Effect on Hover */}
                <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-20 bg-gradient-to-tr from-white/20 via-transparent to-transparent transition-opacity duration-700 pointer-events-none"
                    data-oid="shine-effect"
                />
            </div>

            {/* Logo - Top left corner */}
            {logo && (
                <div className="absolute top-4 left-4 z-20">
                    <OptimizedImage
                        src={logo}
                        alt={`${title} logo`}
                        className={`w-auto object-contain ${
                            id === 'sauna' || id === 'spa'
                                ? 'h-4 md:h-6' // Smaller for NutraSwiss
                                : 'h-14 md:h-16' // Larger for Carbon Mountain Cafe and Restaurant JF
                        } ${id === 'dining' ? 'brightness-0 invert drop-shadow-md' : ''}`} // Make Restaurant Jungfrau logo white with shadow
                        data-oid="activity-logo"
                        priority={priority}
                        hideLoadingSpinner={true}
                        blurDataURL={
                            // Remove blur placeholders for NutraSwiss, Restaurant Jungfrau, and Carbon Mountain Cafe logos
                            (id === 'sauna' || id === 'spa' || id === 'dining' || id === 'bistro_dine_in')
                                ? undefined
                                : getBlurPlaceholder(logo)
                        }
                    />
                </div>
            )}

            {/* Badge - Smaller and more subtle */}
            {badge && (
                <div className="absolute top-4 right-4 z-20 max-w-[80%]">
                    <div className={`text-[8px] md:text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] shadow-md whitespace-nowrap ${
                        badge.includes("Coming Soon") || badge.includes("Bientôt") || badge.includes("Demnächst")
                            ? "bg-yellow-500 text-charcoal-gray"
                            : "bg-alpine-gold/90 text-charcoal-gray"
                    }`}>
                        {badge}
                    </div>
                </div>
            )}

            {/* Content Container - Reduced padding */}
            <div className="absolute inset-0 z-10 p-6 md:p-8 flex flex-col justify-end" data-oid="content-container">
                {/* Gradient overlay only behind text content */}
                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/70 via-black/40 to-transparent pointer-events-none" />
                
                <div className="space-y-2 md:space-y-3 relative z-10" data-oid="text-stack">
                    <div className="space-y-1">
                        {/* Subtitle / Eyebrow - White text for better readability, smaller and less prominent */}
                        {subtitle && (
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                className="text-white font-lato text-[8px] md:text-[9px] tracking-[0.15em] uppercase font-medium mb-1"
                                data-oid="activity-subtitle"
                            >
                                {subtitle}
                            </motion.p>
                        )}

                        {/* Title with Playfair Serif - Reduced size */}
                        <h3
                            className="text-white font-playfair text-xl md:text-2xl leading-[1.1] group-hover:text-alpine-gold transition-colors duration-300 line-clamp-2"
                            data-oid="activity-title"
                        >
                            {title}
                        </h3>
                    </div>

                    {/* Description - Only shown if showDescription is true (for hero card) */}
                    {showDescription && description && (
                        <p
                            className="text-white/70 font-lato text-sm md:text-base max-w-md opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500 ease-in-out"
                            data-oid="activity-description"
                        >
                            {description}
                        </p>
                    )}

                    {/* CTA Link Indicator - Smaller and more compact */}
                    <div
                        className="mt-4 flex items-center gap-2 text-white/90 group-hover:text-alpine-gold transition-all duration-300"
                        data-oid="cta-indicator"
                    >
                        <div
                            className="w-6 h-[2px] bg-alpine-gold/30 group-hover:w-10 group-hover:bg-alpine-gold transition-all duration-500"
                            data-oid="cta-line"
                        />

                        <span
                            className="font-lato font-bold uppercase text-[9px] md:text-[10px] tracking-[0.15em]"
                            data-oid="cta-text"
                        >
                            {t("activities.grid.viewDetails")}
                        </span>
                        <ArrowRight
                            size={12}
                            className="transform -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500"
                            data-oid="cta-icon"
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    );

    if (actionType === "modal") {
        return (
            <button onClick={handleCardClick} className="w-full h-full text-left" data-oid={dataOid}>
                {CardContent}
            </button>
        );
    }

    if (actionType === "external") {
        return (
            <a
                href={destination}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
                data-oid={dataOid}
            >
                {CardContent}
            </a>
        );
    }

    return (
        <Link to={destination || "#"} className="block h-full" data-oid={dataOid}>
            {CardContent}
        </Link>
    );
};

export default ActivityCard;
